import axios from "axios";
import api, { unwrapList, unwrapOne } from "./apiClient";
import { BACKEND_URL } from "@/config/api";

const API_BASE = `${BACKEND_URL}/addresses`;

/**
 * Address Service (AddressController - /addresses)
 * Campos do AddressDTO em português: cep, number, logradouro, complemento,
 * estado, localidade, bairro, uf, principal, latitude, longitude.
 * Tudo é escopado pela empresa do token.
 */
class AddressService {
  /**
   * Endereços da empresa
   * @returns {Promise<Array>}
   */
  async getAllAddresses() {
    try {
      const response = await api.get(API_BASE);
      return unwrapList(response);
    } catch (error) {
      console.error("❌ Erro ao buscar endereços:", error);
      throw error;
    }
  }

  /**
   * @param {number} id
   * @returns {Promise<object|null>}
   */
  async getAddressById(id) {
    try {
      const response = await api.get(`${API_BASE}/${id}`);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar endereço ${id}:`, error);
      throw error;
    }
  }

  /**
   * Endereços de um cliente (pelo e-mail)
   * @param {string} email
   * @returns {Promise<Array>}
   */
  async getByClientEmail(email) {
    try {
      const response = await api.get(
        `${API_BASE}/client/${encodeURIComponent(email)}`,
        { silent: true },
      );
      return unwrapList(response);
    } catch (error) {
      if (error.response?.status === 404) return [];
      console.error(`❌ Erro ao buscar endereços do cliente ${email}:`, error);
      throw error;
    }
  }

  /**
   * @param {object} addressData - AddressDTO
   * @returns {Promise<object|null>}
   */
  async createAddress(addressData) {
    try {
      const response = await api.post(API_BASE, addressData);
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao criar endereço:", error);
      throw error;
    }
  }

  /**
   * @param {number} id
   * @param {object} addressData - AddressDTO
   * @returns {Promise<object|null>}
   */
  async updateAddress(id, addressData) {
    try {
      const response = await api.put(`${API_BASE}/${id}`, addressData);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao atualizar endereço ${id}:`, error);
      throw error;
    }
  }

  /**
   * @param {number} id
   * @returns {Promise<void>}
   */
  async deleteAddress(id) {
    try {
      await api.delete(`${API_BASE}/${id}`);
    } catch (error) {
      console.error(`❌ Erro ao deletar endereço ${id}:`, error);
      throw error;
    }
  }

  /**
   * Define o endereço como principal (PATCH /addresses/{id}/principal)
   * @param {number} id
   * @returns {Promise<object|null>}
   */
  async setPrincipal(id) {
    try {
      const response = await api.patch(`${API_BASE}/${id}/principal`);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao definir endereço ${id} como principal:`, error);
      throw error;
    }
  }

  /**
   * Monta o AddressDTO a partir do formulário
   * @param {object} form
   * @returns {object}
   */
  buildPayload(form) {
    return {
      cep: String(form.cep || "").replace(/\D/g, ""),
      number: String(form.number || "").trim(),
      logradouro: String(form.logradouro || "").trim(),
      complemento: String(form.complemento || "").trim(),
      bairro: String(form.bairro || "").trim(),
      localidade: String(form.localidade || "").trim(),
      estado: String(form.estado || "").trim(),
      uf: String(form.uf || "")
        .trim()
        .toUpperCase(),
      principal: !!form.principal,
    };
  }

  /**
   * Validate CEP format (Brazilian postal code)
   * @param {string} cep
   * @returns {boolean}
   */
  isValidCEP(cep) {
    return String(cep || "").replace(/\D/g, "").length === 8;
  }

  /**
   * Format CEP with mask (12345-678)
   * @param {string} cep
   * @returns {string}
   */
  formatCEP(cep) {
    const cleanCEP = String(cep || "").replace(/\D/g, "");
    if (cleanCEP.length !== 8) return cep || "";
    return cleanCEP.replace(/(\d{5})(\d{3})/, "$1-$2");
  }

  /**
   * Valida o formulário de endereço (nomes do AddressDTO)
   * @param {object} addressData
   * @returns {{valid: boolean, errors: object}}
   */
  validateAddressData(addressData) {
    const errors = {};
    const isBlank = (value) => !String(value || "").trim();

    if (isBlank(addressData.logradouro)) {
      errors.logradouro = "Rua é obrigatória";
    }
    if (isBlank(addressData.number)) {
      errors.number = "Número é obrigatório";
    }
    if (isBlank(addressData.bairro)) {
      errors.bairro = "Bairro é obrigatório";
    }
    if (isBlank(addressData.localidade)) {
      errors.localidade = "Cidade é obrigatória";
    }
    if (isBlank(addressData.uf)) {
      errors.uf = "UF é obrigatória";
    } else if (String(addressData.uf).trim().length !== 2) {
      errors.uf = "UF deve ter 2 caracteres";
    }
    if (isBlank(addressData.cep)) {
      errors.cep = "CEP é obrigatório";
    } else if (!this.isValidCEP(addressData.cep)) {
      errors.cep = "CEP inválido (deve ter 8 dígitos)";
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors,
    };
  }

  /**
   * Busca o endereço pelo CEP no ViaCEP.
   * Usa o axios puro (sem interceptors): o JWT nunca vai para terceiros.
   * Retorna os nomes do ViaCEP (iguais aos do AddressDTO) e aliases em inglês.
   * @param {string} cep
   * @returns {Promise<object>}
   */
  async searchByCEP(cep) {
    try {
      const cleanCEP = String(cep || "").replace(/\D/g, "");
      if (cleanCEP.length !== 8) {
        throw new Error("CEP inválido");
      }

      const response = await axios.get(
        `https://viacep.com.br/ws/${cleanCEP}/json/`,
      );

      if (response.data.erro) {
        throw new Error("CEP não encontrado");
      }

      const data = {
        cep: cleanCEP,
        logradouro: response.data.logradouro || "",
        bairro: response.data.bairro || "",
        localidade: response.data.localidade || "",
        uf: response.data.uf || "",
        estado: response.data.estado || "",
      };

      // Aliases em inglês usados pela calculadora de frete
      return {
        ...data,
        street: data.logradouro,
        neighborhood: data.bairro,
        city: data.localidade,
        state: data.uf,
        zipCode: cleanCEP,
      };
    } catch (error) {
      console.error("❌ Erro ao buscar CEP:", error);
      throw error;
    }
  }
}

// Export singleton instance
const addressService = new AddressService();
export default addressService;
